import { DomSanitizer } from '@angular/platform-browser';

import { of, pipe } from 'rxjs';
import { map, mergeAll, mergeMap, shareReplay } from 'rxjs/operators';

import {
  VisualizationData,
  VisualizationMapperOperator,
  VisualizeOperatorInput,
  VisualizeOperator,
  ReportsTranslations,
  ReportDashboardComponentViewData,
  ReportDashboardMetricOptions,
} from '@core/reports-types';
import { DurationPipe } from '@core/user-interface-legacy';
import { pipeCompose } from '@core/utils-legacy';

import {
  applyAmountOfDataSetting,
  applyAmountOfDataSettingPipe,
} from './apply-amount-of-data-setting-pipe';
import { normalize, normalizePipe } from './normalizePipe';
import { breakdown, breakdownBlueprints } from '../breakdown';
import { funnel, funnelBlueprints } from '../funnel';
import { singleMetric, singleMetricBlueprints } from '../single-metric';
import { timeBased, timeBasedBlueprints } from '../time-based';
import { trend, trendBlueprints } from '../trend';
import { matchBlueprintFnFactory } from './from-blueprints';

export const applyDefaultGroupping = (
  component: ReportDashboardComponentViewData,
  metric: ReportDashboardMetricOptions,
): ReportDashboardComponentViewData => {
  if (!metric.defaultGroup || metric.defaultGroup === component.primaryGroup) {
    return component;
  }
  return {
    ...component,
    primaryGroup: metric.defaultGroup,
    secondaryGroup: component.primaryGroup,
  };
};

const applyDefaultGrouppingPipe = pipe(
  map((input: VisualizeOperatorInput) => ({
    ...input,
    component: applyDefaultGroupping(input.component, input.metric),
  })),
);

export const visualize = (...mappers: VisualizationMapperOperator[]) =>
  pipe(
    applyDefaultGrouppingPipe,
    applyAmountOfDataSettingPipe,
    normalizePipe,
    mergeMap(({ component, dataset, metric, meta }) =>
      mappers.map(mapper => of({ component, dataset, metric, meta }).pipe(mapper)),
    ),
    mergeAll(),
    shareReplay(1),
  );

// TODO This has very ugly input type. And it should take library/template components as well
export const mapToVisualization = (
  sanitizer: DomSanitizer,
  translations: ReportsTranslations,
  durationPipe: DurationPipe,
  clampTrendEndToToday = false,
): VisualizeOperator =>
  pipe(
    visualize(
      trend(sanitizer, translations, clampTrendEndToToday),
      breakdown(sanitizer, translations),
      singleMetric(sanitizer, translations, durationPipe),
      funnel(sanitizer, translations, durationPipe),
      timeBased(sanitizer, translations, durationPipe),
    ),
  );

/**
 * Must be run in the injection context
 */
export const buildVisualizationData = (
  params: VisualizeOperatorInput,
  {
    sanitizer,
    translations,
    durationPipe,
    clampTrendEndToToday = false,
  }: {
    sanitizer: DomSanitizer;
    translations: ReportsTranslations;
    durationPipe: DurationPipe;
    clampTrendEndToToday?: boolean;
  },
): VisualizationData<any> => {
  const inputData = pipeCompose(
    () => ({
      ...params,
      component: applyDefaultGroupping(params.component, params.metric),
    }),
    applyAmountOfDataSetting,
    normalize,
  );

  const blueprints = [
    ...breakdownBlueprints(sanitizer, translations),
    ...singleMetricBlueprints(sanitizer, translations, durationPipe),
    ...funnelBlueprints(sanitizer, translations, durationPipe),
    ...timeBasedBlueprints(sanitizer, translations, durationPipe),
    ...trendBlueprints(sanitizer, translations, clampTrendEndToToday),
  ];

  const { component, meta } = inputData;
  const strategy = blueprints.find(blueprint =>
    matchBlueprintFnFactory(blueprint)({
      component,
      meta,
    }),
  );
  if (!strategy) {
    throw new Error(
      `No blueprint found for component.id: ${(<any>component).id} (kind: ${
        component.kind
      }, viewMode: ${component.viewMode}, chartType: ${component.chartType})`,
    );
  }
  return strategy.factory(inputData);
};
